// ContactEditFormBody — the edit-mode <form> body extracted verbatim from
// ContactEditModal.tsx's left-column ternary (the third arm). Identity / CRM /
// enrichment field rows, additional-emails list, Lists chips, the read-only
// Shopify panel, the ContactFactsSection mount, and the Cancel/Save footer.
//
// The form onSubmit stays wired to the orchestrator's handleSubmit (passed as a
// prop). Pure presentational leaf: no state/effects of its own. Markup/handlers/
// inline styles/a11y are byte-identical to the inline original. No `fetch` here.
import React from 'react';
import { ContactFactsSection } from '../../ContactFactsSection';
import type { ContactListDef } from '../../contactLists';
import type { ContactObject } from '../lib/types';
import type { UserProfile } from '@/types/session';

export interface ContactEditFormBodyProps {
  handleSubmit: (e: React.FormEvent) => void;
  columnColor: string | undefined;
  contact: ContactObject | null | undefined;
  isNew: boolean | undefined;
  isSaving: boolean | undefined;
  onClose: () => void;
  onArchive?: (id: string) => void;
  profile: UserProfile | null | undefined;
  // field state
  firstName: string;
  setFirstName: (s: string) => void;
  lastName: string;
  setLastName: (s: string) => void;
  email: string;
  setEmail: (s: string) => void;
  additionalEmails: string[];
  setAdditionalEmails: (v: string[]) => void;
  phone: string;
  setPhone: (s: string) => void;
  company: string;
  setCompany: (s: string) => void;
  xHandle: string;
  setXHandle: (s: string) => void;
  lists: string[] | undefined;
  setLists: React.Dispatch<React.SetStateAction<string[]>>;
  listDefs: ContactListDef[];
  enrichmentSummary: string;
  enrichHints: string;
  setEnrichHints: (s: string) => void;
  showDealValue: boolean;
  dealValue: string;
  handleDealValueChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  contactTitle: string;
  setContactTitle: (s: string) => void;
  firm: string;
  setFirm: (s: string) => void;
  location: string;
  setLocation: (s: string) => void;
  linkedinUrl: string;
  setLinkedinUrl: (s: string) => void;
  contactSource: string;
  setContactSource: (s: string) => void;
  enrichmentTags: string[];
}

export function ContactEditFormBody({
  handleSubmit, columnColor, contact, isNew, isSaving, onClose, onArchive, profile,
  firstName, setFirstName, lastName, setLastName, email, setEmail, additionalEmails,
  setAdditionalEmails, phone, setPhone, company, setCompany, xHandle, setXHandle, lists, setLists,
  listDefs, enrichmentSummary, enrichHints, setEnrichHints, showDealValue, dealValue,
  handleDealValueChange, contactTitle, setContactTitle, firm, setFirm, location, setLocation,
  linkedinUrl, setLinkedinUrl, contactSource, setContactSource, enrichmentTags,
}: ContactEditFormBodyProps) {
  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={(e) => {
        // Swallow Enter on single-line inputs so it doesn't trigger the
        // native form submit (which closes the modal). Textareas keep
        // their newline behavior, and submit buttons still work. (#1440)
        if (
          e.key === 'Enter' &&
          !e.shiftKey &&
          e.target instanceof HTMLElement &&
          e.target.tagName === 'INPUT' &&
          (e.target as HTMLInputElement).type !== 'submit' &&
          (e.target as HTMLInputElement).type !== 'button'
        ) {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
      }}
      className="contact-edit-form overflow-y-auto flex-1"
    >
      {/* Contact info block - tinted inset card with column color */}
      <div style={{
        background: columnColor || 'var(--color-surface-tinted)',
        border: `1px solid ${columnColor ? 'rgba(0,0,0,0.08)' : 'var(--color-border-tinted)'}`,
        borderRadius: '12px',
        margin: '10px 16px 0 16px',
        padding: '16px',
      }}>
      {/* Total Spent - prominent display for Shopify customers */}
      {(() => {
        const attrs = contact?.attributes || {};
        const totalSpent = contact?.shopifyTotalSpent ?? attrs.shopifyTotalSpent;
        if (!totalSpent || totalSpent <= 0) return null;
        return (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-green-700">Total Spent (Shopify)</span>
              <span className="text-xl font-bold text-green-800">
                ${totalSpent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        );
      })()}

      {/* First Name / Last Name */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">First Name *</label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="John"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
            style={{ outline: 'none' }}
            autoFocus
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">Last Name</label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Doe"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
            style={{ outline: 'none' }}
          />
        </div>
      </div>

      {/* Email / Phone */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
            style={{ outline: 'none' }}
          />
          {additionalEmails.map((em, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <input
                type="email"
                value={em}
                onChange={(e) => setAdditionalEmails(additionalEmails.map((v, j) => (j === i ? e.target.value : v)))}
                placeholder="email@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                style={{ outline: 'none' }}
              />
              <button
                type="button"
                onClick={() => setAdditionalEmails(additionalEmails.filter((_, j) => j !== i))}
                title="Remove email"
                style={{ flexShrink: 0, width: 34, border: '1px solid var(--color-border)', borderRadius: 8, background: 'none', color: 'var(--color-text-faint)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setAdditionalEmails([...additionalEmails, ''])}
            style={{ marginTop: 6, fontSize: 12, fontWeight: 500, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 1px' }}
          >
            + Add email
          </button>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">Phone</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 555 123 4567"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
            style={{ outline: 'none' }}
          />
        </div>
      </div>

      {/* Company / X Handle */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">Company</label>
          <input
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="Company name"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
            style={{ outline: 'none' }}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">X Handle</label>
          <input
            type="text"
            value={xHandle}
            onChange={(e) => setXHandle(e.target.value)}
            placeholder="@username"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
            style={{ outline: 'none' }}
          />
        </div>
      </div>

      {/* Lists — toggle-chip per available list definition */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">Lists</label>
        <div className="flex flex-wrap gap-2">
          {(listDefs ?? []).map((d) => {
            const on = (lists ?? []).includes(d.id);
            return (
              <button
                type="button"
                key={d.id}
                onClick={() => setLists((prev) =>
                  (prev ?? []).includes(d.id)
                    ? (prev ?? []).filter((x) => x !== d.id)
                    : [...(prev ?? []), d.id]
                )}
                className={`px-3 py-1 rounded-full text-sm border transition-colors ${on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
              >
                {d.label}
              </button>
            );
          })}
          {(listDefs ?? []).length === 0 && (
            <span className="text-sm text-gray-400">No lists yet</span>
          )}
        </div>
      </div>

      {/* AI Hints — only show if no enrichment data yet */}
      {!enrichmentSummary && (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">AI Hints</label>
        <input
          type="text"
          value={enrichHints}
          onChange={(e) => setEnrichHints(e.target.value)}
          placeholder="Keywords to help AI find this person (e.g. Stanford, fintech, NYC)"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
          style={{ outline: 'none' }}
        />
      </div>
      )}

      {/* Deal Value */}
      {showDealValue && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">Deal Value</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="text"
                inputMode="numeric"
                value={dealValue}
                onChange={handleDealValueChange}
                placeholder="0"
                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg bg-white"
                style={{ outline: 'none' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Profile & Enrichment */}
      <div className="mb-4">
        <div
          className="flex items-center gap-2 mb-2 cursor-pointer select-none"
          onClick={() => {
            const el = document.getElementById('enrichment-section');
            if (el) el.style.display = el.style.display === 'none' ? '' : 'none';
          }}
        >
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Profile & Enrichment</span>
          <span className="text-xs text-gray-400">&#9662;</span>
        </div>
        <div id="enrichment-section" style={{ display: (linkedinUrl || contactTitle || firm || location || contactSource || enrichmentTags.length > 0) ? '' : 'none' }}>
          {/* Title / Firm */}
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1 pl-1">Title</label>
              <input type="text" value={contactTitle} onChange={(e) => setContactTitle(e.target.value)}
                placeholder="e.g. Managing Director" className="w-full px-3 py-1.5 border border-gray-300 rounded-lg bg-white text-sm" style={{ outline: 'none' }} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1 pl-1">Firm</label>
              <input type="text" value={firm} onChange={(e) => setFirm(e.target.value)}
                placeholder="Firm / Fund name" className="w-full px-3 py-1.5 border border-gray-300 rounded-lg bg-white text-sm" style={{ outline: 'none' }} />
            </div>
          </div>
          {/* Location / LinkedIn */}
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1 pl-1">Location</label>
              <input type="text" value={location} onChange={(e) => setLocation(e.target.value)}
                placeholder="City, State" className="w-full px-3 py-1.5 border border-gray-300 rounded-lg bg-white text-sm" style={{ outline: 'none' }} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1 pl-1">LinkedIn</label>
              <input type="url" value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)}
                placeholder="https://linkedin.com/in/..." className="w-full px-3 py-1.5 border border-gray-300 rounded-lg bg-white text-sm" style={{ outline: 'none' }} />
            </div>
          </div>
          {/* Source */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-500 mb-1 pl-1">Source</label>
            <input type="text" value={contactSource} onChange={(e) => setContactSource(e.target.value)}
              placeholder="How they found us" className="w-full px-3 py-1.5 border border-gray-300 rounded-lg bg-white text-sm" style={{ outline: 'none' }} />
          </div>
          {/* Enrichment Tags */}
          {enrichmentTags.length > 0 && (
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-500 mb-1 pl-1">AI Tags</label>
              <div className="flex flex-wrap gap-1">
                {enrichmentTags.map((tag, i) => (
                  <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs">{tag}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>


      </div>

      {/* Shopify Purchase History - read-only in edit mode */}
      {(() => {
        const attrs = contact?.attributes || {};
        const shopifyOrders = contact?.shopifyOrders || attrs.shopifyOrders || [];
        const shopifyTotalSpent = contact?.shopifyTotalSpent ?? attrs.shopifyTotalSpent;
        const shopifyOrderCount = contact?.shopifyOrderCount ?? attrs.shopifyOrderCount;
        const shopifyStores = contact?.shopifyStores || attrs.shopifyStores || [];
        if (!shopifyOrders || shopifyOrders.length === 0) return null;
        return (
          <div style={{ margin: '12px 16px 0 16px' }}>
          <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex items-center gap-2 text-sm font-medium text-purple-800 mb-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              Purchase History
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm text-purple-900">
              <div className="flex justify-between">
                <span className="text-purple-600">Total Spent:</span>
                <span className="font-semibold">${shopifyTotalSpent?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-purple-600">Orders:</span>
                <span>{shopifyOrderCount || shopifyOrders.length}</span>
              </div>
            </div>
            {shopifyStores && shopifyStores.length > 0 && (
              <div className="text-xs text-purple-600 mt-2">Stores: {shopifyStores.join(', ')}</div>
            )}
          </div>
          </div>
        );
      })()}

      {/* Structured Details — entity facts driven by predicate registry (#1605) */}
      {!isNew && contact?.id && (
        <div style={{ padding: '0 20px' }}>
          <ContactFactsSection contactId={contact.id} authToken={(profile as { authToken?: string | null } | null | undefined)?.authToken ?? null} />
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 20px 20px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          {!isNew && contact?.id && onArchive && (
            <button type="button" onClick={() => { onArchive(contact.id); onClose(); }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, background: 'transparent', border: 'none', borderRadius: 6, color: 'var(--color-text-faint)', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-action-archive-bg)'; e.currentTarget.style.color = 'var(--color-action-archive-text)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9ca3af'; }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/>
              </svg>
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
          <button type="button" onClick={onClose}
            style={{ padding: '8px 16px', borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s', background: 'var(--color-action-cancel-bg)', border: '1px solid var(--color-action-cancel-border)', color: 'var(--color-action-cancel-text)', minWidth: 80 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-action-cancel-bg-hover)'; e.currentTarget.style.borderColor = 'var(--color-action-cancel-border-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-action-cancel-bg)'; e.currentTarget.style.borderColor = 'var(--color-action-cancel-border)'; }}
          >Cancel</button>
          <button type="submit" disabled={isSaving}
            style={{ padding: '8px 16px', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: isSaving ? 'not-allowed' : 'pointer', transition: 'all 0.2s', background: 'var(--color-brand)', border: 'none', color: 'white', opacity: isSaving ? 0.5 : 1, minWidth: 80 }}
            onMouseEnter={(e) => { if (!isSaving) e.currentTarget.style.background = '#0284c7'; }}
            onMouseLeave={(e) => { if (!isSaving) e.currentTarget.style.background = 'var(--color-brand)'; }}
          >{isSaving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </form>
  );
}
